import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { ButtonComponent, ToggleButtonsComponent } from '@cumlaude/shared-components-buttons';
import { RAfwezigheidsreden } from '@cumlaude/service-contract';
import { RestService } from '@cumlaude/shared-services';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, forkJoin, firstValueFrom } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SaveableBeheerDashboard } from '../save-guard/save-guard';
import { omit } from 'lodash-es';
import { ToastrService } from '@cumlaude/shared-components-toast';

interface Afwezigheidsreden extends RAfwezigheidsreden {
	originalValue: boolean;
}

@Component({
	selector: 'app-afwezigheidsredenen',
	templateUrl: './afwezigheidsredenen.component.html',
	styleUrls: ['./afwezigheidsredenen.component.scss'],
	imports: [ButtonComponent, ToggleButtonsComponent],
})
export class AfwezigheidsredenenComponent implements SaveableBeheerDashboard {
	private readonly restService = inject(RestService);
	private readonly toastr = inject(ToastrService);

	private readonly refresh$ = new BehaviorSubject<void>(undefined);

	readonly dirty = computed(() =>
		this.afwezigheidsredenen().some((afwezigheidsreden) => afwezigheidsreden.aanwezig !== afwezigheidsreden.originalValue)
	);

	readonly options = signal([true, false]);

	readonly afwezigheidsredenen = linkedSignal(
		toSignal(
			this.refresh$.pipe(
				switchMap(() => this.restService.getAfwezigheidsredenen()),
				map((afwezigheidsredenen) =>
					afwezigheidsredenen.map((afwezigheidsreden) => ({
						...afwezigheidsreden,
						originalValue: afwezigheidsreden.aanwezig,
					}))
				)
			),
			{
				initialValue: [],
			}
		)
	);

	protected revert() {
		this.afwezigheidsredenen.set(
			this.afwezigheidsredenen().map((afwezigheidsreden) => ({ ...afwezigheidsreden, aanwezig: afwezigheidsreden.originalValue }))
		);
	}

	protected async trySave(): Promise<void> {
		const changedAfwezigheidsredenen = this.afwezigheidsredenen().filter(
			(afwezigheidsreden) => afwezigheidsreden.aanwezig !== afwezigheidsreden.originalValue
		);

		if (changedAfwezigheidsredenen.length === 0) return;

		const updates$ = changedAfwezigheidsredenen.map((afwezigheidsreden) =>
			this.restService.putAfwezigheidsreden(omit(afwezigheidsreden, 'originalValue'))
		);

		try {
			await firstValueFrom(forkJoin(updates$));
			this.refresh$.next();
			this.toastr.success('Redenen succesvol opgeslagen');
		} catch (error) {
			console.error(error);
			this.toastr.error('Er is een fout opgetreden bij het opslaan van de redenen');
		}
	}

	protected update(afwezigheidsreden: Afwezigheidsreden, newValue: boolean) {
		const afwezigheidsredenen = this.afwezigheidsredenen();
		const index = afwezigheidsredenen.findIndex((a) => a.id === afwezigheidsreden.id);
		this.afwezigheidsredenen.set([
			...afwezigheidsredenen.slice(0, index),
			{ ...afwezigheidsreden, aanwezig: newValue },
			...afwezigheidsredenen.slice(index + 1),
		]);
	}

	getLabel = (aanwezig: boolean): string => {
		return aanwezig ? 'Aanwezig' : 'Afwezig';
	};
}
